import React from 'react'
import { BrowserRouter as Router, Route, Routes } from "react-router-dom"
import styles from './App.module.scss'
import Home from './pages/Home'
import NavBar, { HamburgerState } from './components/NavBar/NavBar'
import AppInsightsRouter from './components/AppInsightsRouter/AppInsightsRouter'
import SettingsPage from './pages/SettingsPage'
import PricingPage from './pages/PricingPage'
import TaskCreate from './pages/Tasks/TaskCreate'
import TaskShow from './pages/Tasks/TaskShow'
import SuppliersCreate from './pages/Suppliers/SupplierCreate'
import SupplierShow from './pages/Suppliers/SupplierShow'
import SupplierEdit from './pages/Suppliers/SupplierEdit'
import CreateLocation from './pages/Locations/LocationCreate'
import LocationShow from './pages/Locations/LocationShow'
import LocationEdit from './pages/Locations/LocationEdit'
import SupplierList from './pages/Suppliers/Suppliers'
import CarriersCreate from './pages/Carriers/CreateCarrier'
import CarrierEdit from './pages/Carriers/EditCarrier'
import CarrierShow from './pages/Carriers/ShowCarrier'
import CarrierList from './pages/Carriers/CarrierList'
import Login from './pages/Login/Login'
import { appInsightsConfig, fetchAppInsightsConfig } from './config/applicationInsights'
import { ai, getAppInsights } from './plugins/telemetry/telemetryService'
import PrivateRoute from './components/PrivateRoute/PrivateRoute'
import Orders from './pages/Orders/Orders'
import OrderCreate from './pages/Orders/OrderCreate'
import OrderEdit from './pages/Orders/OrderEdit'
import TerminalShow from './pages/Terminals/TerminalShow'
import TerminalsCreate from './pages/Terminals/TerminalCreate'
import TerminalEdit from './pages/Terminals/TerminalEdit'
import TerminalList from './pages/Terminals/TerminalList'
import CarrierRateEdit from './pages/Carriers/CarrierRateEdit'
import Locations from './pages/Locations/Locations'
import ReportPage from './pages/ReportPage'
import Customers from './pages/Customers/Customers'
import CustomerShow from './pages/Customers/CustomerShow'
import CustomerCreate from './pages/Customers/CustomerCreate'
import CustomerEdit from './pages/Customers/CustomerEdit'
import TasksReport from './pages/Reports/Tasks'
import BasisTypeEdit from './pages/BasisType/BasisTypeEdit'
import BasisTypeList from './pages/BasisType/BasisTypeList'
import BasisTypeShow from './pages/BasisType/BasisTypeShow'
import BasisTypePriceAdmin from './pages/BasisType/BasisTypePriceAdmin'
import CreateDirectMarkup from './pages/DirectMarkup/DirectMarkupCreate'
import EditDirectMarkup from './pages/DirectMarkup/DirectMarkupEdit'
import DirectMarkupList from './pages/DirectMarkup/DirectMarkupList'
import EditIndexContract from './pages/IndexContracts/Edit/EditIndexContract'
import IndexContractList from './pages/IndexContracts/IndexContractList'
import MassMarkup from './pages/Markup/MassMarkup'
import PrePurchaseAgreementCreate from './pages/PrePurchaseAgreements/PrePurchaseAgreementCreate'
import PrePurchaseAgreementEdit from './pages/PrePurchaseAgreements/PrePurchaseAgreementEdit'
import PrePurchaseAgreementList from './pages/PrePurchaseAgreements/PrePurchaseAgreementList'
import SalesAgreementCreate from './pages/SalesAgreements/SalesAgreementCreate'
import SalesAgreementEdit from './pages/SalesAgreements/SalesAgreementEdit'
import SalesAgreements from './pages/SalesAgreements/SalesAgreements'
import SwapDealCreate from './pages/SwapDeals/SwapDealCreate'
import SwapDealEdit from './pages/SwapDeals/SwapDealEdit'
import SwapDealList from './pages/SwapDeals/SwapDeals'
import TermContractCreate from './pages/TermContracts/TermContractCreate'
import TermContractEdit from './pages/TermContracts/TermContractEdit'
import CreateBasisType from './pages/BasisType/BasisTypeCreate'
import CreateIndexContract from './pages/IndexContracts/Create/CreateIndexContract'
import TermContracts from './pages/TermContracts/TermContracts'
import ContractVolumeCreate from './pages/ContractVolumes/ContractVolumeCreate'
import ContractVolumeEdit from './pages/ContractVolumes/ContractVolumeEdit'
import ContractVolumes from './pages/ContractVolumes/ContractVolumes'
import FileNumberCreate from './pages/FileNumbers/FileNumberCreate'
import FileNumbers from './pages/FileNumbers/FileNumbers'
import FileNumberEdit from './pages/FileNumbers/FileNumberUpdate'
import PatCosts from './pages/PatCost/PatCosts'
import PostingCreate from './pages/Postings/PostingCreate'
import PostingEdit from './pages/Postings/PostingEdit'
import Postings from './pages/Postings/Postings'
import QuickbooksAuthenticate from './pages/Quickbooks/QuickbooksAuthenticate'
import StateTaxes from './pages/StateTaxes/StateTaxes'
import UserCreate from './pages/Users/UserCreate'
import UserEdit from './pages/Users/UserEdit'
import Users from './pages/Users/Users'
import UpdateCarrierPointToPointRates from './pages/Carriers/PointToPoint/UpdatePointToPointRates'
import { H } from 'highlight.run';
import { ToastContainer } from 'react-toastify'
import 'react-toastify/dist/ReactToastify.css';

interface Props {

}

interface State {
    mainMenuIsOpen: boolean
    subMenuIsOpen: boolean
    lightHamburgerState: boolean
    isAuthenticated: boolean
}

export default class App extends React.Component<Props, State> {
    appInsights = null;

    state = {
        mainMenuIsOpen: false,
        subMenuIsOpen: false,
        lightHamburgerState: false,
        isAuthenticated: true,
    }

    async componentDidMount() {
        // Fetch Application Insights configuration from backend
        await fetchAppInsightsConfig();

        // Initialize Application Insights if enabled and connection string is available
        if (appInsightsConfig.enabled && appInsightsConfig.connectionString) {
            ai.initialize(appInsightsConfig.connectionString);
            this.appInsights = getAppInsights();
        }
    }

    setMainMenuState(desiredState: boolean) {
        this.setState({ mainMenuIsOpen: desiredState })
    }

    setSubMenuState(desiredState: boolean) {
        this.setState({ subMenuIsOpen: desiredState })

        if (!desiredState) {
            this.setState({ lightHamburgerState: false })
        }
    }

    triggerLightMenuState() {
        this.setState({ lightHamburgerState: true })
    }

    render() {
        const hamburgerState: HamburgerState = {
            setMainMenuState: this.setMainMenuState.bind(this),
            setSubMenuState: this.setSubMenuState.bind(this),
            triggerLightHamburger: this.triggerLightMenuState.bind(this),
            mainMenuState: this.state.mainMenuIsOpen,
            subMenuState: this.state.subMenuIsOpen,
            lightHamburgerState: this.state.lightHamburgerState
        }

        return (
            <div className={styles.app}>
                <Router>
                    {<NavBar hamburgerState={hamburgerState} />}
                    <AppInsightsRouter>
                        <Routes>
                            <Route path="login" element={<Login {...this.props} />} />

                            <Route element={<PrivateRoute />}  >
                                <Route path="/" element={<Home />} />
                                <Route path="orders">
                                    <Route index element={<Orders />} />
                                    <Route path="new" element={<OrderCreate />} />
                                    <Route path="edit/:orderKey" element={<OrderEdit />} />
                                </Route>
                                <Route path="customers">
                                    <Route index element={<Customers />} />
                                    <Route path="new" element={<CustomerCreate />} />
                                    <Route path="edit/:customerKey" element={<CustomerEdit />} />
                                    <Route path="show/:customerKey" element={<CustomerShow />} />
                                </Route>
                                <Route path="locations">
                                    <Route index element={<Locations />} />
                                    <Route path="create/:customerKey" element={<CreateLocation />} />
                                    <Route path="edit/:locationKey" element={<LocationEdit />} />
                                    <Route path="show/:locationKey" element={<LocationShow />} />
                                </Route>
                                <Route path="carriers">
                                    <Route index element={<CarrierList />} />
                                    <Route path="new" element={<CarriersCreate />} />
                                    <Route path="edit/:carrierKey" element={<CarrierEdit />} />
                                    <Route path="show/:carrierKey" element={<CarrierShow />} />
                                    <Route path="rate/:carrierRateKey" element={<CarrierRateEdit />} />
                                    <Route path="point-to-point/:carrierPointToPointRateDateSpanKey" element={<UpdateCarrierPointToPointRates />} />
                                </Route>
                                <Route path="tasks">
                                    <Route path="new" element={<TaskCreate />} />
                                    <Route path="show/:taskKey" element={<TaskShow />} />
                                </Route>
                                <Route path="suppliers">
                                    <Route index element={<SupplierList />} />
                                    <Route path="new" element={<SuppliersCreate />} />
                                    <Route path="edit/:supplierKey" element={<SupplierEdit />} />
                                    <Route path="show/:supplierKey" element={<SupplierShow />} />
                                </Route>
                                <Route path="terminals">
                                    <Route index element={<TerminalList />} />
                                    <Route path="show/:terminalKey" element={<TerminalShow />} />
                                    <Route path="new" element={<TerminalsCreate />} />
                                    <Route path="edit/:terminalKey" element={<TerminalEdit />} />
                                </Route>




                                <Route path='/reports' element={
                                    <ReportPage hamburgerState={hamburgerState} {...this.props} />}
                                >

                                    <Route path='task-details' element={<TasksReport />} />

                                </Route>

                                <Route path="pricing" element={
                                    <PricingPage hamburgerState={hamburgerState} {...this.props} />}
                                >
                                    <Route path='mass-markup' element={<MassMarkup />} />

                                    <Route path='basis-types' element={<BasisTypeList />} />
                                    <Route path='basis-types/admin' element={<BasisTypePriceAdmin />} />

                                    <Route path='basis-types/new' element={<CreateBasisType />} />

                                    <Route path='basis-types/show/:basisTypeKey' element={<BasisTypeShow />} />
                                    <Route path='basis-types/edit/:basisTypeKey' element={<BasisTypeEdit />} />

                                    <Route path='index-contracts' element={<IndexContractList />} />
                                    <Route path='index-contracts/new' element={<CreateIndexContract />} />
                                    <Route path='index-contracts/edit/:indexContractKey' element={<EditIndexContract />} />

                                    <Route path='sales-agreements/new' element={<SalesAgreementCreate />} />
                                    <Route path='sales-agreements/edit/:salesAgreementKey' element={<SalesAgreementEdit />} />
                                    <Route path='sales-agreements/' element={<SalesAgreements />} />

                                    <Route path='pre-purchase-agreements' element={<PrePurchaseAgreementList />} />
                                    <Route path='pre-purchase-agreements/new' element={<PrePurchaseAgreementCreate />} />
                                    <Route path='pre-purchase-agreements/edit/:prePurchaseAgreementKey' element={<PrePurchaseAgreementEdit />} />

                                    <Route path='direct-markup' element={<DirectMarkupList />} />
                                    <Route path='direct-markup/edit/:directMarkupKey' element={<EditDirectMarkup />} />
                                    <Route path='direct-markup/new' element={<CreateDirectMarkup />} />

                                    <Route path='term-contract' element={<TermContracts />} />
                                    <Route path='term-contract/new' element={<TermContractCreate />} />
                                    <Route path='term-contract/edit/:termContractKey' element={<TermContractEdit />} />

                                    <Route path='swap-deals' element={<SwapDealList />} />
                                    <Route path='swap-deals/new' element={<SwapDealCreate />} />
                                    <Route path='swap-deals/edit/:swapDealKey' element={<SwapDealEdit />} />
                                </Route>

                                <Route path="settings" element={
                                    <SettingsPage hamburgerState={hamburgerState} {...this.props} />}
                                >
                                    <Route path="pat-costs" element={<PatCosts />} />

                                    <Route path="state-taxes" element={<StateTaxes />} />

                                    <Route path="file-number" element={<FileNumbers />} />
                                    <Route path="file-number/new" element={<FileNumberCreate />} />
                                    <Route path="file-number/edit/:fileNumberKey" element={<FileNumberEdit />} />

                                    <Route path="users" element={<Users />} />
                                    <Route path="users/new" element={<UserCreate />} />
                                    <Route path="users/edit/:userKey" element={<UserEdit />} />

                                    <Route path='postings' element={<Postings />} />
                                    <Route path='postings/new' element={<PostingCreate />} />
                                    <Route path='postings/edit/:postingKey' element={<PostingEdit />} />

                                    <Route path='contract-volumes' element={<ContractVolumes />} />
                                    <Route path='contract-volumes/edit/:contractVolumeKey' element={<ContractVolumeEdit />} />
                                    <Route path='contract-volumes/new' element={<ContractVolumeCreate />} />

                                    <Route path='quickbooks' element={<QuickbooksAuthenticate />} />
                                </Route>
                            </Route>

                        </Routes>
                    </AppInsightsRouter>
                </Router>
                <ToastContainer />
            </div>
        )
    }
}